import React, { useState } from 'react';
import OnboardingLayout from '../../OnboardingLayout';
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { CreditCard, Landmark, Shield, Check } from 'lucide-react';
import { loadStripe } from "@/services/stripeService";
import { useEffect } from 'react';

interface PaymentSetupProps {
  formData: any;
  updateFormData: (data: any) => void;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
}

const PaymentSetup: React.FC<PaymentSetupProps> = ({
  formData,
  updateFormData,
  currentStep,
  totalSteps,
  onNext,
  onPrevious
}) => {
  const [paymentMethod, setPaymentMethod] = useState(formData.paymentMethod || 'card');
  const [stripeReady, setStripeReady] = useState(false);
  const [cardDetails, setCardDetails] = useState({
    number: '',
    expiry: '',
    cvc: '',
    name: ''
  });
  
  useEffect(() => {
    const initStripe = async () => {
      await loadStripe();
      setStripeReady(true);
    };
    
    initStripe();
  }, []);
  
  const handleSubmit = () => {
    updateFormData({ 
      paymentMethod,
      stripeTokenId: 'mock_token_for_demo' // In a real app, this would be a Stripe token
    });
    onNext();
  };
  
  const handleCardDetailsChange = (field: string, value: string) => {
    setCardDetails({
      ...cardDetails,
      [field]: value
    });
  };

  return (
    <OnboardingLayout
      title="Payment Method Setup"
      subtitle="Add a payment method to subscribe to your favorite journalists"
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={handleSubmit}
      onPrevious={onPrevious}
      nextLabel="Save & Continue"
    >
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6 px-4 py-3 bg-blue-50 border border-blue-100 rounded-lg dark:bg-blue-900/20 dark:border-blue-800">
          <div className="flex items-center">
            <Shield className="h-5 w-5 text-blue-500 mr-3" />
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Secure Payment Processing</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-xs text-blue-600 dark:text-blue-400"
            onClick={() => onNext()}
          >
            Add payment later
          </Button>
        </div>
        
        <Card className="mb-8">
          <CardContent className="pt-6">
            <h3 className="text-lg font-medium mb-4">Select Payment Method</h3>
            
            <RadioGroup 
              value={paymentMethod}
              onValueChange={setPaymentMethod}
              className="space-y-4"
            >
              <div className={`border rounded-lg p-4 ${
                paymentMethod === 'card' ? 'border-primary bg-primary/5' : ''
              }`}>
                <div className="flex items-start">
                  <RadioGroupItem value="card" id="card" className="mt-1" />
                  <div className="ml-3 flex-1">
                    <Label htmlFor="card" className="font-medium">Credit or Debit Card</Label>
                    <div className="mt-4 space-y-3">
                      <div>
                        <Label htmlFor="card-number">Card Number</Label>
                        <Input 
                          id="card-number" 
                          placeholder="1234 5678 9012 3456" 
                          className="mt-1"
                          value={cardDetails.number}
                          onChange={(e) => handleCardDetailsChange('number', e.target.value)}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="expiry">Expiration Date</Label>
                          <Input 
                            id="expiry" 
                            placeholder="MM/YY" 
                            className="mt-1"
                            value={cardDetails.expiry}
                            onChange={(e) => handleCardDetailsChange('expiry', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label htmlFor="cvc">CVC</Label>
                          <Input 
                            id="cvc" 
                            placeholder="123" 
                            className="mt-1"
                            value={cardDetails.cvc}
                            onChange={(e) => handleCardDetailsChange('cvc', e.target.value)}
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="card-name">Name on Card</Label>
                        <Input 
                          id="card-name" 
                          placeholder="John Smith" 
                          className="mt-1"
                          value={cardDetails.name}
                          onChange={(e) => handleCardDetailsChange('name', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                  <CreditCard className="h-6 w-6 text-muted-foreground ml-2 mt-1" />
                </div>
              </div>
              
              <div className={`border rounded-lg p-4 ${
                paymentMethod === 'bank' ? 'border-primary bg-primary/5' : ''
              }`}>
                <div className="flex items-start">
                  <RadioGroupItem value="bank" id="bank" className="mt-1" />
                  <div className="ml-3">
                    <Label htmlFor="bank" className="font-medium">Bank Account (ACH)</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Connect your bank account for direct payments
                    </p>
                  </div>
                  <Landmark className="h-6 w-6 text-muted-foreground ml-auto mt-1" />
                </div>
              </div>
              
              <div className={`border rounded-lg p-4 ${
                paymentMethod === 'paypal' ? 'border-primary bg-primary/5' : ''
              }`}>
                <div className="flex items-start">
                  <RadioGroupItem value="paypal" id="paypal" className="mt-1" />
                  <div className="ml-3">
                    <Label htmlFor="paypal" className="font-medium">PayPal</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Pay using your PayPal account
                    </p>
                  </div>
                  <div className="ml-auto mt-1 font-bold text-blue-500">
                    Pay<span className="text-blue-700">Pal</span>
                  </div>
                </div>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>
        
        <div className="bg-muted/30 rounded-lg p-4">
          <h4 className="text-sm font-medium mb-2">Why add a payment method?</h4>
          <p className="text-sm text-muted-foreground mb-3">
            Adding a payment method allows you to:
          </p>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li className="flex items-start">
              <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5" />
              Subscribe to premium content from your favorite journalists
            </li>
            <li className="flex items-start">
              <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5" />
              Make one-time micropayments for individual articles
            </li>
            <li className="flex items-start">
              <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5" />
              Support quality journalism directly
            </li>
          </ul>
          <p className="text-sm text-muted-foreground mt-3">
            Your payment information is securely encrypted and stored by Stripe.
            DirectPress never stores your full payment details.
          </p>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default PaymentSetup;
